import React, { Fragment, useMemo } from "react"

import { useGlobalStore } from "@Stores/global"
import InfoTip from "components/InfoTip"
import InputTip from "components/InputTip"

const WISHPOND = { value: 'wishpond', label: 'Wishpond' }
const AWEBER = { value: 'aweber', label: 'Aweber' }
const AIB = { value: 'aib', label: 'All Inbox' }
const SENDLANE = { value: 'sendlane', label: 'Sendlane' }

const HTTP_GET = { value: 'http_get', label: 'Http GET' }
const HTTP_POST = { value: 'http_post', label: 'Http POST' }
const TWYNE = { value: 'twyne', label: 'Twyne' }
const TWYNE_DIRECT_POST = { value: 'twyne_direct_post', label: 'Twyne Direct Post' }
const TWYNE_OPTOUT = { value: 'twyne_optout', label: 'Twyne' }

const MIDPATH = { value: 'midpath', label: "Path" }
const TCPA = { value: 'tcpa', label: "Survey / TCPA" }
const REGISTRATION = { value: 'registration', label: "Registration" }
const DELAYED_REGISTRATION = { value: 'delayed_registration', label: "Registration Delayed" }
const EMAIL = { value: 'email', label: "Email" }
const PUSHNOTIFICATION = { value: 'pushnotification', label: "Push" }
const RETARGETING = { value: 'retargeting', label: "Retargeting Pixels" }
const CALLCENTER = { value: 'callcenter', label: "Calls" }
const TWYNE_REGISTRATION = { value: 'twyne_registration', label: "Form Offers" }
const CLICKWALL = { value: 'clickwall', label: "ClickWall" }
const EXIT = { value: 'exit', label: "Exit" }
const ADSENSE = { value: 'adsense', label: "Adsense" }
const CMSPAGE = { value: 'cmspage', label: 'CMS Pages' }
const SMS = { value: 'sms', label: 'SMS' }

const TRUSTED_FORM = { value: 'trusted_form', label: 'Trusted form' }
const TRUSTED_FORM_EXTEND = { value: 'trusted_form_extend', label: 'Trusted form Extend' }
const SFTP = { value: 'sftp', label: 'Secure FTP (SFTP)' }
const FTP = { value: 'ftp', label: 'FTP' }
const SCA = { value: 'sca', label: 'SCA' }

const DEFAULT_ORIGIN = { value: 'default', label: 'Use origin from pekey' }
const OVERRIDE_ORIGIN = { value: '', label: '-- Override Origin --', disabled: true }

const GOOGLE_ADS = { value: 'google_ads', label: 'Google Ads' }

const REWARD_PROGRESSION= { value: 'reward_progression', label: 'Reward' }

const VENDORS = {
  delayed_register: [TWYNE, HTTP_GET, HTTP_POST],
  register: [TWYNE, TWYNE_DIRECT_POST, TRUSTED_FORM, TRUSTED_FORM_EXTEND, HTTP_GET, HTTP_POST, REWARD_PROGRESSION],
  revenue_postback: [DEFAULT_ORIGIN, OVERRIDE_ORIGIN, MIDPATH, TCPA, REGISTRATION, DELAYED_REGISTRATION, EMAIL, PUSHNOTIFICATION, RETARGETING, CALLCENTER, TWYNE_REGISTRATION, CLICKWALL, EXIT, ADSENSE, CMSPAGE, SMS],
  revenue_poller: [DEFAULT_ORIGIN, OVERRIDE_ORIGIN, MIDPATH, TCPA, REGISTRATION, DELAYED_REGISTRATION, EMAIL, PUSHNOTIFICATION, RETARGETING, CALLCENTER, TWYNE_REGISTRATION, CLICKWALL, EXIT, ADSENSE, CMSPAGE, SMS],
  unsubscribe: [WISHPOND, AWEBER, AIB, SENDLANE, HTTP_GET, HTTP_POST],
  optout: [TWYNE_OPTOUT, HTTP_GET, HTTP_POST],
  contest_finish: [EMAIL, SFTP, FTP, SCA],
  sweeps_confirmation: [TWYNE, HTTP_GET, HTTP_POST],
  sweeps_confirmation_ticket: [TWYNE, HTTP_GET, HTTP_POST],
  revenue_share: [GOOGLE_ADS, HTTP_GET, HTTP_POST],
  path_impression: [HTTP_GET, HTTP_POST, TWYNE, REWARD_PROGRESSION],
  path_ping_post: [TWYNE],
  lead_status_sync: [TWYNE, HTTP_GET, HTTP_POST],
}

export function useVendors({ integration_type: type }) {
  return useMemo(() => VENDORS[type], [type])
}

const TYPES = [
  { label: 'Delayed Registration', value: 'delayed_register' },
  { label: 'Register', value: 'register' },
  { label: 'Revenue Postback', value: 'revenue_postback' },
  { label: 'Revenue Poller', value: 'revenue_poller' },
  { label: 'Revenue Share', value: 'revenue_share' },
  { label: 'Unsubscribe', value: 'unsubscribe' },
  { label: 'Opt-Out', value: 'optout' },
  { label: 'Sweeps Contest Finished', value: 'contest_finish', sweeps: true },
  { label: 'Sweeps E-mail Confirmation', value: 'sweeps_confirmation', sweeps: true },
  { label: 'Sweeps Ticket Confirmation', value: 'sweeps_confirmation_ticket', sweeps: true },
  { label: 'Path Impression', value: 'path_impression' },
  { label: 'Path Ping/Post', value: 'path_ping_post' },
  { label: 'Lead Status Sync', value: 'lead_status_sync' },
]

export const TYPE_LABELS = Object.fromEntries(TYPES.map(({ value, label }) => [value, label]))

export function useTypes() {
  const { siteSettings: { hasSweeps: checkHasSweeps } } = useGlobalStore()

  return useMemo(() => {
    const hasSweeps = checkHasSweeps()
    return TYPES
      .filter(({ sweeps }) => !sweeps || hasSweeps)
      .map(({ label, value }) => ({ label, value }))
  }, [checkHasSweeps])
}

const revenuePostbackParamsTips = {
  sid: {
    key: <span>
      Required. <br />
      For internal use.
    </span>,
    value: <span>
      Type: Integer. <br />
      Do not change this.
    </span>
  },
  nid: {
    key: <span>
      Required. <br />
      For internal use.
    </span>,
    value: <span>
      Type: Integer. <br />
      Do not change this.
    </span>
  },
  pekey: {
    key: <span>
      Required. <br />
      The pekey is available on placements.
    </span>,
    value: <span>
      This mergetag should be replaced programmatically with the pekey associated to the revenue.
    </span>
  },
  revenue: {
    key: <span>
      Required. <br />
      The amount of money generated.
    </span>,
    value: <span>
      Type: Float. <br />
      This mergetag should be replaced programmatically with a float value.
    </span>
  },
  subid: {
    key: <span>
      Optional. <br />
      This param can be used for group revenues beside of the pekey level.
    </span>,
    value: <span>
      Type: Alphanumeric. <br />
      This param could be a static or programmatic value.
    </span>
  },
  transid: {
    key: <span>
      Optional. <br />
      Use this param when you want to distinguish revenues pieces above the pekey level.
    </span>,
    value: <span>
      Type: Alphanumeric. <br />
      This param should be a programmatic value. Make sure it is a unique value to distinguish the transaction, like the click ID.
    </span>,
  },
  format: {
    key: <span>
      Optional. <br />
      Use this param when you want to add a 1x1 transparent image.
    </span>,
    value: <span>
      Type: String. <br />
      This param should be "image" to return a 1x1 transparent image.
    </span>,
  },
}

export function useRevenuePostbackParamsTips(param) {
  const { [param]: { key, value } } = revenuePostbackParamsTips

  const KeyTip = useMemo(() => !key ? Fragment : props => <InputTip
    {...props}
    id={`key-${param}`}
    tip={key}
    icon={<InfoTip />}
  />, [])

  const ValueTip = useMemo(() => !value ? Fragment : props => <InputTip
    {...props}
    id={`value-${param}`}
    tip={value}
    icon={<InfoTip />}
  />, [])

  return {
    KeyTip,
    ValueTip,
  }
}

export const ACTION_OPTIONS = [
  { label: 'Next Page', value: 'nextPage' },
  { label: 'Finish', value: 'finish' },
]

export const PATH_IMPRESSION_EXTRA_RULES = [{
  'value': 'page_id',
  'label': 'Page',
  'type': 'page',
  'group': 'Traffic',
}, {
  'value': 'slot_position',
  'label': 'Slot position',
  'type': 'integer',
  'group': 'Traffic',
}, {
  'value': 'action',
  'label': 'Action',
  'type': 'action',
  'group': 'Traffic',
}, {
  'value': 'completed',
  'label': 'Completed',
  'type': 'boolean',
  'group': 'Traffic',
}, {
  'value': 'tcpa_isTCPA',
  'label': 'is TCPA',
  'type': 'boolean',
  'group': 'TCPA',
}, {
  'value': 'tcpa_version',
  'label': 'Version',
  'type': 'input',
  'group': 'TCPA',
}, {
  'value': 'tcpa_field',
  'label': 'Field',
  'type': 'input',
  'group': 'TCPA',
}, {
  'value': 'tcpa_answer',
  'label': 'Answer',
  'type': 'input',
  'group': 'TCPA',
}, {
  'value': 'path_user_profile_id',
  'label': 'Path User Profile',
  'type': 'pathUserProfile',
  'group': 'Traffic',
}, {
  'value': 'session_revenue',
  'label': 'Session Revenue',
  'type': 'integer',
  'group': 'Traffic',
}, {
  'value': 'impression_revenue',
  'label': 'Impression Revenue',
  'type': 'integer',
  'group': 'Traffic',
}]

export const TCPA_CONSENT_DBA_RULE = {
  value: 'tcpaConsentDba',
  label: 'TCPA Consent DBA',
  type: 'tcpaConsentDba',
  group: 'Traffic',
}

export const PATH_PING_POST_RULES = [{
  'value': 'bid',
  'label': 'Bid',
  'type': 'input',
  'group': 'Traffic',
}, {
  'value': 'answer',
  'label': 'Answer',
  'type': 'input',
  'group': 'Traffic',
},
  TCPA_CONSENT_DBA_RULE,
]

export const TRANSACTION_VALUE_RULE = {
  value: 'transactionValue',
  label: 'Transaction Value',
  type: 'float',
  group: 'Traffic',
}
